import { DateTime } from 'luxon'
import { Activity, OutdoorEngagement } from '../../../../../api/models'
import { type EngagementClip, type RecordingViewedDetails, ViewedIn } from '../types'
import {
  type BaseEventCompat,
  RRWEB_EVENTS,
  RRWEB_VIDEO_PLACEHOLDER_NAMES,
  upgradeEvent,
  type VideoEndEvent_V2,
  type VideoMirrorEndEvent,
  type VideoMirrorStartEvent,
  type VideoStartEvent_V2,
  type VideoStateChangeEvent
} from '@simplisafe/compass-web'
import { EventType, eventWithTime } from '@rrweb/types'
import { CROSS_NAV_PLAYER_PREFIX, STACKED_PLAYER_PREFIX } from '../../constants'

// Identifies a single viewing across its VIDEO_START/VIDEO_END events (mirrors getVideoIdentifier).
export const viewingIdentifier = (cameraId: string, placeholderName: string | null, startTimeEpoch: number): string =>
  `${cameraId}|${placeholderName}|${startTimeEpoch}`

// The only rrweb events Inspect consumes - Used as the assembly-time filter
// (assembleAndValidateRecordingsFromSegments) so the rest of the
// session recording (DOM snapshots, canvas mirror frames, etc) is discarded
const INSPECT_VIEWING_EVENT_TAGS: ReadonlySet<string> = new Set<string>([
  RRWEB_EVENTS.VIDEO_START,
  RRWEB_EVENTS.VIDEO_STATE_CHANGE,
  RRWEB_EVENTS.VIDEO_END,
  RRWEB_EVENTS.VIDEO_MIRROR_START,
  RRWEB_EVENTS.VIDEO_MIRROR_END
])

export const isInspectViewingEvent = (event: eventWithTime): boolean =>
  event.type === EventType.Custom && INSPECT_VIEWING_EVENT_TAGS.has(event.data.tag)

// Recording start/stop markers are dropped by isInspectViewingEvent but processActivities needs them
// for dead-space detection, so ErtInspect tracks them separately while filtering.
export const isRecordingStartedOrStoppedEvent = (event: eventWithTime): boolean =>
  event.type === EventType.Custom && (event.data.tag === RRWEB_EVENTS.RECORDING_STARTED || event.data.tag === RRWEB_EVENTS.RECORDING_STOPPED)

export type ViewingInterval = { start: number; end: number }

// A playback-position fix from a VIDEO_STATE_CHANGE: position (seconds into the recording) at an
// interaction epoch, plus the speed/paused state that governs footage advance until the next anchor.
type PositionAnchor = { atEpoch: number; positionSeconds: number; paused: boolean; speed: number }

type ParsedViewing = {
  cameraId: string
  videoPlaceholderName: string | null
  isLive: boolean
  interactionStart: number
  interactionEnd: number | null
  startSpeed: number
  anchorEpoch: number | null
  durationMillis: number | null
  anchors: PositionAnchor[]
}

type MirrorSpan = {
  cameraId: string
  videoPlaceholderName: string | null
  startTimeEpoch: number
  interactionStart: number
  interactionEnd: number
}

// Playback position (seconds into the recording) at an interaction epoch, reconstructed from the
// state-change anchors. Before the first anchor, work backward at the START speed - this recovers the
// initial timeline-click seek, which no event records (VIDEO_START hardcodes currentTimeSeconds: 0 and the
// seek fires before first play). With no anchors, assume position 0 at start (the timeline snaps clicks
// near a recording's start to 0). Footage freezes while paused and clamps to [0, durationMillis].
const positionSecondsAt = (viewing: ParsedViewing, atEpoch: number): number => {
  const maxSeconds = viewing.durationMillis === null ? Number.POSITIVE_INFINITY : viewing.durationMillis / 1000
  const clamp = (position: number) => Math.min(Math.max(position, 0), maxSeconds)

  const first = viewing.anchors[0]
  if (!first) {
    return clamp((viewing.startSpeed * (atEpoch - viewing.interactionStart)) / 1000)
  }
  if (atEpoch <= first.atEpoch) {
    return clamp(first.positionSeconds - (viewing.startSpeed * (first.atEpoch - atEpoch)) / 1000)
  }
  let current = first
  for (const anchor of viewing.anchors) {
    if (anchor.atEpoch > atEpoch) break
    current = anchor
  }
  if (current.paused) return clamp(current.positionSeconds)
  return clamp(current.positionSeconds + (current.speed * (atEpoch - current.atEpoch)) / 1000)
}

// Footage epoch shown at an interaction epoch. Live viewings (and, defensively, recorded viewings missing
// recordingDetails) show the present: footage == interaction time.
const footageEpochAt = (viewing: ParsedViewing, atEpoch: number): number =>
  viewing.isLive || viewing.anchorEpoch === null ? atEpoch : viewing.anchorEpoch + positionSecondsAt(viewing, atEpoch) * 1000

// Min/max playback position over an interaction window: the bounds, every anchor inside the window, and
// each such anchor's LEFT-LIMIT - the position reached just before it. A loop restart or backward seek
// resets the position at the anchor itself, so a footage peak between anchors exists only as that
// left-limit (real case: the Feed stacked player loops its recording, resetting to 0 at each restart).
const positionRangeOver = (viewing: ParsedViewing, fromEpoch: number, toEpoch: number): { min: number; max: number } => {
  const positions = [positionSecondsAt(viewing, fromEpoch), positionSecondsAt(viewing, toEpoch)]
  for (const anchor of viewing.anchors) {
    if (anchor.atEpoch <= fromEpoch || anchor.atEpoch >= toEpoch) continue
    positions.push(anchor.positionSeconds, positionSecondsAt(viewing, anchor.atEpoch - 1))
  }
  return { min: Math.min(...positions), max: Math.max(...positions) }
}

/**
 * Parse the raw event stream into per-camera, footage-time interval sets:
 * - Stream intervals: each viewing's footage window.
 * - Main intervals: Feed viewings (stacked players whole-window; the cross-nav live player until the first
 *   main-canvas mirror event) plus each mirror-to-main-canvas span mapped through its matched viewing.
 * - Main viewing ids: the viewingIdentifier of every VIDEO_START-backed viewing that contributed at least
 *   one Main interval - lets a caller holding raw VideoStartEvents (e.g. timelineUtils.ts) test a specific
 *   event for Main-canvas coverage without re-deriving footage-time intervals itself.
 * Viewings are keyed by the full viewing identifier (cameraId | videoPlaceholderName | startTimeEpoch);
 * the first START opens a viewing (restarts share the memoized epoch), the LATEST VIDEO_END closes it, and
 * a dangling-open viewing or mirror span closes at the last recorded event.
 */
export const buildViewingIntervals = (
  events: eventWithTime[],
  // The full recording's true last event epoch. Callers passing a FILTERED event stream (Inspect keeps
  // only the video custom events) must supply it - otherwise dangling viewings/mirror spans would close
  // at the last surviving event instead of the recording's real end.
  fullRecordingLastEventEpoch?: number
): {
  mainIntervalsByCameraId: Map<string, ViewingInterval[]>
  streamIntervalsByCameraId: Map<string, ViewingInterval[]>
  mainViewingIds: Set<string>
} => {
  const mainIntervalsByCameraId = new Map<string, ViewingInterval[]>()
  const streamIntervalsByCameraId = new Map<string, ViewingInterval[]>()
  const mainViewingIds = new Set<string>()

  let lastEventEpoch = fullRecordingLastEventEpoch ?? 0
  for (const event of events) {
    if (event.timestamp > lastEventEpoch) lastEventEpoch = event.timestamp
  }

  const pushInterval = (map: Map<string, ViewingInterval[]>, cameraId: string, start: number, end: number): boolean => {
    if (end <= start) return false
    const arr = map.get(cameraId) ?? []
    arr.push({ start, end })
    map.set(cameraId, arr)
    return true
  }

  const viewingsById = new Map<string, ParsedViewing>()
  const mirrorSpans: MirrorSpan[] = []
  let openMirror: Omit<MirrorSpan, 'interactionEnd'> | null = null
  let firstMirrorEpoch = Number.POSITIVE_INFINITY

  for (const event of events) {
    if (event.type !== EventType.Custom) continue
    const tag = event.data.tag

    if (tag === RRWEB_EVENTS.VIDEO_START) {
      const payload = event.data.payload as BaseEventCompat
      upgradeEvent(payload, RRWEB_EVENTS.VIDEO_START)
      const start = payload as VideoStartEvent_V2
      const id = viewingIdentifier(start.cameraId, start.videoPlaceholderName, start.startTimeEpoch)
      if (!viewingsById.has(id)) {
        viewingsById.set(id, {
          cameraId: start.cameraId,
          videoPlaceholderName: start.videoPlaceholderName,
          isLive: start.isLive,
          interactionStart: start.startTimeEpoch,
          interactionEnd: null,
          startSpeed: start.speed || 1,
          anchorEpoch: start.recordingDetails ? DateTime.fromISO(start.recordingDetails.timestamp).toMillis() : null,
          durationMillis: start.recordingDetails?.durationMillis ?? null,
          anchors: []
        })
      }
    } else if (tag === RRWEB_EVENTS.VIDEO_STATE_CHANGE) {
      const payload = event.data.payload as BaseEventCompat
      upgradeEvent(payload, RRWEB_EVENTS.VIDEO_STATE_CHANGE)
      const change = payload as VideoStateChangeEvent
      const viewing = viewingsById.get(viewingIdentifier(change.cameraId, change.videoPlaceholderName, change.startTimeEpoch))
      if (viewing && change.currentTimeSeconds != null) {
        viewing.anchors.push({
          atEpoch: event.timestamp,
          positionSeconds: change.currentTimeSeconds,
          paused: change.paused,
          speed: change.speed || 1
        })
        if (change.recordingDetails?.durationMillis != null) viewing.durationMillis = change.recordingDetails.durationMillis
      }
    } else if (tag === RRWEB_EVENTS.VIDEO_END) {
      const payload = event.data.payload as BaseEventCompat
      upgradeEvent(payload, RRWEB_EVENTS.VIDEO_END)
      const end = payload as VideoEndEvent_V2
      const viewing = viewingsById.get(viewingIdentifier(end.cameraId, end.videoPlaceholderName, end.startTimeEpoch))
      if (viewing && end.endTimeEpoch != null && (viewing.interactionEnd === null || end.endTimeEpoch > viewing.interactionEnd)) {
        viewing.interactionEnd = end.endTimeEpoch
      }
    } else if (tag === RRWEB_EVENTS.VIDEO_MIRROR_START) {
      const mirror = event.data.payload as VideoMirrorStartEvent
      if (mirror.videoMirrorPlaceholderName !== RRWEB_VIDEO_PLACEHOLDER_NAMES.MAIN_VIDEO_CANVAS) continue
      firstMirrorEpoch = Math.min(firstMirrorEpoch, event.timestamp)
      if (openMirror) mirrorSpans.push({ ...openMirror, interactionEnd: event.timestamp })
      openMirror = {
        cameraId: mirror.cameraId,
        videoPlaceholderName: mirror.videoPlaceholderName,
        startTimeEpoch: mirror.startTimeEpoch,
        interactionStart: event.timestamp
      }
    } else if (tag === RRWEB_EVENTS.VIDEO_MIRROR_END) {
      const mirror = event.data.payload as VideoMirrorEndEvent
      if (mirror.videoMirrorPlaceholderName !== RRWEB_VIDEO_PLACEHOLDER_NAMES.MAIN_VIDEO_CANVAS) continue
      firstMirrorEpoch = Math.min(firstMirrorEpoch, event.timestamp)
      if (openMirror) {
        mirrorSpans.push({ ...openMirror, interactionEnd: event.timestamp })
        openMirror = null
      }
    }
  }
  if (openMirror) mirrorSpans.push({ ...openMirror, interactionEnd: lastEventEpoch })

  for (const [id, viewing] of viewingsById.entries()) {
    const interactionClose = viewing.interactionEnd ?? lastEventEpoch

    let footageStart: number
    let footageEnd: number
    if (viewing.isLive || viewing.anchorEpoch === null) {
      footageStart = viewing.interactionStart
      footageEnd = interactionClose
    } else {
      // The footage window is the min/max position seen across the whole viewing (anchors and their
      // left-limits included, so a backward scrub or loop restart still extends the window).
      const { min, max } = positionRangeOver(viewing, viewing.interactionStart, interactionClose)
      footageStart = viewing.anchorEpoch + min * 1000
      footageEnd = viewing.anchorEpoch + max * 1000
    }
    pushInterval(streamIntervalsByCameraId, viewing.cameraId, footageStart, footageEnd)

    const placeholder = viewing.videoPlaceholderName ?? ''
    if (placeholder.startsWith(STACKED_PLAYER_PREFIX)) {
      if (pushInterval(mainIntervalsByCameraId, viewing.cameraId, footageStart, footageEnd)) mainViewingIds.add(id)
    } else if (placeholder.startsWith(CROSS_NAV_PLAYER_PREFIX)) {
      const feedEnd = Math.min(firstMirrorEpoch, interactionClose)
      if (
        pushInterval(mainIntervalsByCameraId, viewing.cameraId, footageEpochAt(viewing, viewing.interactionStart), footageEpochAt(viewing, feedEnd))
      ) {
        mainViewingIds.add(id)
      }
    }
  }

  for (const span of mirrorSpans) {
    const id = viewingIdentifier(span.cameraId, span.videoPlaceholderName, span.startTimeEpoch)
    const viewing = viewingsById.get(id)
    if (!viewing) {
      // No matching viewing (its START predates the recording or was lost). Live viewings show the present,
      // so the interaction window is the best available footage estimate.
      pushInterval(mainIntervalsByCameraId, span.cameraId, span.interactionStart, span.interactionEnd)
      continue
    }
    if (viewing.isLive || viewing.anchorEpoch === null) {
      // Use the mirror span's own bounds, not the matched viewing's interactionEnd: real capture showed a
      // live viewing's VIDEO_END firing ~16ms before its matching VIDEO_MIRROR_START (cause unconfirmed -
      // e.g. a transient stream drop - not necessarily anything ending for real). Clamping to a VIDEO_END
      // at/before the mirror start would zero out (or shrink) an otherwise-valid mirror span.
      if (pushInterval(mainIntervalsByCameraId, span.cameraId, Math.max(viewing.interactionStart, span.interactionStart), span.interactionEnd)) {
        mainViewingIds.add(id)
      }
    } else {
      // Footage covered while on the canvas: min/max position over the span, anchors and their left-limits
      // included, so a scrub or loop restart during the span colors the whole covered range.
      const { min, max } = positionRangeOver(viewing, span.interactionStart, span.interactionEnd)
      if (pushInterval(mainIntervalsByCameraId, span.cameraId, viewing.anchorEpoch + min * 1000, viewing.anchorEpoch + max * 1000)) {
        mainViewingIds.add(id)
      }
    }
  }

  return { mainIntervalsByCameraId, streamIntervalsByCameraId, mainViewingIds }
}

/**
 * Subdivide a footage window into contiguous viewed-state segments. At each sub-segment: Main when its
 * midpoint falls inside a Main interval, else Mini inside a Stream interval, else NotViewed. Intervals
 * are effectively clipped to [windowStart, windowEnd) and adjacent equal-state segments are merged.
 */
export const segmentViewedWindow = (
  mainIntervals: ViewingInterval[],
  streamIntervals: ViewingInterval[],
  windowStart: number,
  windowEnd: number
): RecordingViewedDetails[] => {
  if (windowEnd <= windowStart) return []

  const boundaries = new Set<number>([windowStart, windowEnd])
  for (const interval of [...mainIntervals, ...streamIntervals]) {
    if (interval.start > windowStart && interval.start < windowEnd) boundaries.add(interval.start)
    if (interval.end > windowStart && interval.end < windowEnd) boundaries.add(interval.end)
  }
  const sorted = [...boundaries].sort((a, b) => a - b)

  const covers = (intervals: ViewingInterval[], point: number) => intervals.some(i => i.start <= point && point < i.end)

  const merged: RecordingViewedDetails[] = []
  for (let i = 0; i < sorted.length - 1; i++) {
    const startTimeEpoch = sorted[i]
    const endTimeEpoch = sorted[i + 1]
    const mid = (startTimeEpoch + endTimeEpoch) / 2
    const viewedIn = covers(mainIntervals, mid) ? ViewedIn.Main : covers(streamIntervals, mid) ? ViewedIn.Mini : ViewedIn.NotViewed

    const last = merged[merged.length - 1]
    if (last && last.viewedIn === viewedIn && last.endTimeEpoch === startTimeEpoch) {
      last.endTimeEpoch = endTimeEpoch
    } else {
      merged.push({ startTimeEpoch, endTimeEpoch, viewedIn })
    }
  }
  return merged
}

// Museum V2 /recordings began populating `agentViewStart` in QA (first observed 2026-07-30, KVS clips).
// Keep the API-first check and the videoRecordings-derived fallback for clips where it is still absent
// (e.g. TEP clips observed without it on the same engagement).
//
// Observed gap shapes driving the tolerance below:
// - TEP (VDP) clips: the archived recording starts at the motion trigger, but the specialist's Holodeck
//   session doesn't start playing until the TEP livestream connects - 8-10s after clip start in QA.
// - KVS clips: the matching videoRecordings entry can land BEFORE the clip's own startTime - usually 1-2s
//   (clock jitter) but up to preroll-sized offsets (7s observed in QA; the preroll access policy allows
//   ~30s), which must still count as "at start", not a false gap.
// 30s = the preroll policy bound. Over-matching here is benign by construction: a pre-start match clamps to
// the clip start (gap 0, no stripe), and stripes only ever come from timestamps AFTER clip start, which
// this pre-window tolerance doesn't affect. Under-matching is the worse error - it fabricates a
// "customer only" claim for footage the specialist actually had.
const MATCH_TOLERANCE_MS = 30_000

// Below this gap, don't render a hatched head - it's within the team's accepted ±2s clock tolerance
// (doubled for safety margin), not a real "specialist didn't have this footage yet" gap. Prevents sliver
// stripes from KVS jitter alone.
const MIN_STRIPE_GAP_MS = 3000

// The engagement's end epoch: the latest activity completion time - the same definition of "ended" ErtQA's
// EngagementDetails uses for duration (the resolving activity). Inspect bounds its timeline and playback
// here so QA review only ever shows footage from within the engagement. This is when the engagement was
// CLEARED, which can be well past the 20-minute specialist video cutoff (holodeck hides video at
// createdTimestamp + 20min while the specialist finishes clearing). Museum keeps returning clips recorded
// between the cutoff and clearance; markViewedDetailsPastVideoCutoff stripes that stretch NotAvailable on
// the viewed bar. Null when no activity has completed.
export const getEngagementEndEpoch = (activities: Activity[]): number | null => {
  let end: number | null = null
  for (const activity of activities) {
    const completedTimestamp = activity.completed?.timestamp
    if (!completedTimestamp) continue
    const epoch = DateTime.fromISO(completedTimestamp).toMillis()
    if (end === null || epoch > end) end = epoch
  }
  return end
}

// A clip is playable iff it STARTS before the engagement ended. A straddling clip (started before the end,
// still recording after) is kept but truncated at the boundary during playback; clips starting at/after the
// end are dropped entirely.
export const isClipPlayable = (clip: Pick<EngagementClip, 'startTime'>, engagementEndEpoch: number): boolean =>
  DateTime.fromISO(clip.startTime).toMillis() < engagementEndEpoch

/**
 * Derive, per clip, the epoch at which the specialist's Holodeck session actually started showing that
 * clip's footage - the "agent view start". Prefers the API's own value; falls back to matching the clip
 * against engagement.videoRecordings when the API hasn't sent one.
 *
 * @param videoRecordings engagement.videoRecordings - per-camera-event timestamps (TEP/KVS/MIST etc.)
 * @param clips flattened per-clip records (see getEngagementRecordings)
 * @returns Map from recordingId to the agent-view-start epoch clamped into the clip window, or null when
 *   no evidence exists that the footage ever reached Holodeck.
 */
export const getAgentViewStartEpochByRecordingId = (
  videoRecordings: OutdoorEngagement['videoRecordings'],
  clips: EngagementClip[]
): Map<string, number | null> => {
  const result = new Map<string, number | null>()

  for (const clip of clips) {
    const clipStartEpoch = DateTime.fromISO(clip.startTime).toMillis()
    const clipEndEpoch = DateTime.fromISO(clip.endTime).toMillis()
    const clampToClip = (epoch: number) => Math.max(clipStartEpoch, Math.min(epoch, clipEndEpoch))

    // API-first: museum's own value wins when present, even though QA hasn't observed it populated yet.
    if (clip.agentViewStart) {
      result.set(clip.recordingId, clampToClip(DateTime.fromISO(clip.agentViewStart).toMillis()))
      continue
    }

    // Fallback: same-camera videoRecordings entries whose timestamp falls in
    // [clipStart - MATCH_TOLERANCE_MS, clipEnd]. Multiple can match (e.g. more than one event landed in
    // a clip's window) - the earliest is the agent's actual first view.
    const candidateEpochs = videoRecordings
      .filter(vr => vr.cameraId === clip.cameraId)
      .map(vr => DateTime.fromISO(vr.timestamp).toMillis())
      .filter(epoch => epoch >= clipStartEpoch - MATCH_TOLERANCE_MS && epoch <= clipEndEpoch)

    // No candidates - this footage has no evidence it ever reached Holodeck (e.g. uploaded after the
    // engagement cleared).
    result.set(clip.recordingId, candidateEpochs.length === 0 ? null : clampToClip(Math.min(...candidateEpochs)))
  }

  return result
}

/**
 * Get the per-clip viewed details.
 *
 * @param events
 * @param clips
 * @param agentViewStartByRecordingId per-clip agent-view-start epochs from getAgentViewStartEpochByRecordingId
 * @returns Map<string, RecordingViewedDetails[]>
 */
export function getRecordingViewedDetails(
  events: eventWithTime[],
  clips: EngagementClip[],
  agentViewStartByRecordingId: Map<string, number | null>,
  // See buildViewingIntervals - required for correctness when `events` is the Inspect-filtered stream.
  fullRecordingLastEventEpoch?: number
): Map<string, RecordingViewedDetails[]> {
  const result = new Map<string, RecordingViewedDetails[]>()
  const { mainIntervalsByCameraId, streamIntervalsByCameraId } = buildViewingIntervals(events, fullRecordingLastEventEpoch)

  for (const clip of clips) {
    const mainIntervals = mainIntervalsByCameraId.get(clip.cameraId) ?? []
    const streamIntervals = streamIntervalsByCameraId.get(clip.cameraId) ?? []
    const viewedDetailsList: RecordingViewedDetails[] = []

    const startTimeEpoch = DateTime.fromISO(clip.startTime).toMillis()
    const endTimeEpoch = DateTime.fromISO(clip.endTime).toMillis()

    // No permission to view this camera - hatch the whole clip regardless of any events.
    if (!clip.accessAllowed) {
      viewedDetailsList.push({ startTimeEpoch, endTimeEpoch, viewedIn: ViewedIn.NotAvailable })
    } else {
      const agentViewStart = agentViewStartByRecordingId.get(clip.recordingId) ?? null
      const gapMs = agentViewStart === null ? null : agentViewStart - startTimeEpoch

      if (agentViewStart === null) {
        // No evidence this footage ever reached Holodeck - hatch the whole clip UNLESS a stream/mirror
        // covered it (direct evidence beats a missing match).
        const segments = segmentViewedWindow(mainIntervals, streamIntervals, startTimeEpoch, endTimeEpoch)
        if (segments.every(segment => segment.viewedIn === ViewedIn.NotViewed)) {
          viewedDetailsList.push({ startTimeEpoch, endTimeEpoch, viewedIn: ViewedIn.NotAvailable })
        } else {
          viewedDetailsList.push(...segments)
        }
      } else if (gapMs !== null && gapMs > MIN_STRIPE_GAP_MS) {
        // Tiger stripe: hatched head [clipStart, agentViewStart) plus a subdivided tail.
        viewedDetailsList.push({ startTimeEpoch, endTimeEpoch: agentViewStart, viewedIn: ViewedIn.NotAvailable })
        viewedDetailsList.push(...segmentViewedWindow(mainIntervals, streamIntervals, agentViewStart, endTimeEpoch))
      } else {
        // Gap at/below the floor - single subdivided window covering the whole clip.
        viewedDetailsList.push(...segmentViewedWindow(mainIntervals, streamIntervals, startTimeEpoch, endTimeEpoch))
      }
    }

    result.set(clip.recordingId, viewedDetailsList)
  }

  return result
}

// Holodeck hides all video from the specialist 20 minutes after the engagement is created; the engagement
// itself can stay open past that while the specialist finishes clearing.
export const SPECIALIST_VIDEO_CUTOFF_MS = 20 * 60_000

// Museum keeps returning clips recorded after the specialist video cutoff (it caps recordings at the
// CLEARANCE time, not the cutoff - observed in QA on 2026-08-04), so any viewed-details stretch past the
// cutoff is footage the specialist could never have seen. Re-mark it NotAvailable (striped), splitting
// intervals that straddle the boundary. Already-NotAvailable intervals pass through whole - they're
// striped either way, and splitting them would only churn keys.
export const markViewedDetailsPastVideoCutoff = (
  viewedDetailsByRecordingId: Map<string, RecordingViewedDetails[]>,
  videoCutoffEpoch: number
): Map<string, RecordingViewedDetails[]> => {
  const result = new Map<string, RecordingViewedDetails[]>()
  for (const [recordingId, details] of viewedDetailsByRecordingId) {
    result.set(
      recordingId,
      details.flatMap(detail => {
        if (detail.endTimeEpoch <= videoCutoffEpoch || detail.viewedIn === ViewedIn.NotAvailable) {
          return [detail]
        }
        if (detail.startTimeEpoch >= videoCutoffEpoch) {
          return [{ ...detail, viewedIn: ViewedIn.NotAvailable }]
        }
        return [
          { ...detail, endTimeEpoch: videoCutoffEpoch },
          { startTimeEpoch: videoCutoffEpoch, endTimeEpoch: detail.endTimeEpoch, viewedIn: ViewedIn.NotAvailable }
        ]
      })
    )
  }
  return result
}
